import styled from '@emotion/styled'
import axios from 'axios'
import { useEffect, useState } from 'react'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import { useTranslation } from 'react-i18next'
import Breakpoint from '../../Breakpoint'
import BadnetTournamentEvent from '../../components/global/BadnetTournamentEvent'
import Container from '../../components/global/Container'
import Loader from '../../components/global/Loader'
import Title from '../../components/global/Title'
import { SERVER_URL } from '../../config'
import { useLangSelected } from '../../hooks/useLangSelected'
import { BadnetTournamentModel } from '../../types/badnetTournament.type'

const TileContent = ({ tournaments }: { tournaments: BadnetTournamentModel[] }) => {
    const truncateText = (text: string, maxLength: number) => {
        if (!text) return ''
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    }

    const cleanHtml = (html: string | null): string => {
        if (!html) return ''
        return html
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim()
    }

    return (
        <>
            <DesktopTileContent>
                {tournaments.slice(0, 3).map((tournament, index) => (
                    <TournamentItem key={tournament.badnetId}>
                        <TournamentName title={tournament.title}>{truncateText(tournament.title, 18)}</TournamentName>
                        <TournamentPlace title={cleanHtml(tournament.place)}>
                            {truncateText(cleanHtml(tournament.place), 12)}
                        </TournamentPlace>
                        {tournament.isComing && <UpcomingIndicator />}
                    </TournamentItem>
                ))}
                {tournaments.length > 3 && <MoreTournaments>+{tournaments.length - 3}</MoreTournaments>}
            </DesktopTileContent>
            <MobileTileContent>
                {tournaments.slice(0, 5).map((tournament, index) => (
                    <TournamentDot key={tournament.badnetId} isComing={tournament.isComing} />
                ))}
                {tournaments.length > 5 && <MoreDots>...</MoreDots>}
            </MobileTileContent>
        </>
    )
}

const BadnetTournaments = () => {
    const [tournaments, setTournaments] = useState<BadnetTournamentModel[] | undefined>(undefined)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [showModal, setShowModal] = useState(false)

    const { t } = useTranslation('translation')

    const lng = useLangSelected()

    const loadTournaments = () =>
        axios
            .get(`${SERVER_URL}/badnet/stored`, {
                params: {
                    ligueId: 3306,
                    lang: lng,
                },
            })
            .then((res) => {
                setTournaments(res.data.data)
            })
            .catch((error) => {
                console.error('Error loading BadNet tournaments:', error)
                setTournaments([])
            })

    useEffect(() => {
        loadTournaments()
    }, [lng])

    // Helper function to format date as YYYY-MM-DD in local timezone
    const formatDateKey = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    // Group tournaments by date for calendar
    const getTournamentsByDate = () => {
        if (!tournaments) return {}
        const grouped: { [key: string]: BadnetTournamentModel[] } = {}

        tournaments.forEach((tournament) => {
            // Use parsedDate for calendar (ISO format), fallback to original date
            const dateStr = tournament.parsedDate || tournament.date

            // Skip tournaments with empty or invalid dates
            if (!dateStr || dateStr === '') {
                console.warn('Empty date for tournament:', tournament.badnetId)
                return
            }

            // Parse date as local time to avoid timezone offset issues
            const [year, month, day] = dateStr.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            // Skip tournaments with invalid dates
            if (isNaN(date.getTime())) {
                console.warn('Invalid date for tournament:', tournament.badnetId, dateStr)
                return
            }
            const dateKey = dateStr

            if (!grouped[dateKey]) {
                grouped[dateKey] = []
            }
            grouped[dateKey].push(tournament)
        })

        return grouped
    }

    const tournamentsByDate = getTournamentsByDate()

    // Get dates with tournaments for calendar highlighting
    const getDatesWithTournaments = () => {
        return Object.keys(tournamentsByDate).map((dateStr) => {
            const [year, month, day] = dateStr.split('-').map(Number)
            return new Date(year, month - 1, day)
        })
    }

    const handleDateClick = (date: Date) => {
        const dateKey = formatDateKey(date)
        if (tournamentsByDate[dateKey] && tournamentsByDate[dateKey].length > 0) {
            setSelectedDate(date)
            setShowModal(true)
        }
    }

    const handleCalendarChange = (value: any) => {
        if (Array.isArray(value)) {
            setSelectedDate(value[0])
        } else if (value instanceof Date) {
            setSelectedDate(value)
        } else {
            setSelectedDate(null)
        }
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedDate(null)
    }

    // Custom tile content to show tournament info directly in calendar cells
    const tileContent = ({ date, view }: { date: Date; view: string }) => {
        if (view === 'month') {
            const dateKey = formatDateKey(date)
            const dayTournaments = tournamentsByDate[dateKey]

            if (dayTournaments && dayTournaments.length > 0) {
                return <TileContent tournaments={dayTournaments} />
            }
        }
        return null
    }

    // Custom tile class for styling
    const tileClassName = ({ date, view }: { date: Date; view: string }) => {
        if (view === 'month') {
            const dateKey = formatDateKey(date)
            if (tournamentsByDate[dateKey]) {
                return 'has-tournaments'
            }
        }
        return null
    }

    if (!tournaments) return <Loader />

    const selectedDateTournaments = selectedDate ? tournamentsByDate[formatDateKey(selectedDate)] || [] : []

    return (
        <Container>
            <Title>{t('page.badnetTournament.title')}</Title>

            <CalendarWrapper>
                <CalendarHeader>
                    <StyledCalendar
                        onChange={handleCalendarChange}
                        onClickDay={handleDateClick}
                        tileContent={tileContent}
                        tileClassName={tileClassName}
                        locale={lng}
                        defaultValue={new Date()}
                    />
                </CalendarHeader>
            </CalendarWrapper>

            {showModal && (
                <ModalOverlay onClick={handleCloseModal}>
                    <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>
                                {selectedDate?.toLocaleDateString(lng, {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </ModalTitle>
                            <CloseButton onClick={handleCloseModal}>{t('common.close')}</CloseButton>
                        </ModalHeader>
                        <ModalBody>
                            {selectedDateTournaments.length > 0 ? (
                                selectedDateTournaments.map((tournament: BadnetTournamentModel) => (
                                    <BadnetTournamentEvent key={tournament.badnetId} tournament={tournament} />
                                ))
                            ) : (
                                <NoData>{t('page.badnetTournament.no-tournaments-day')}</NoData>
                            )}
                        </ModalBody>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    )
}

const CalendarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    margin: 32px 0;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin: 16px 0;
        gap: 16px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        margin: 12px 0;
        gap: 12px;
    }
`

const CalendarHeader = styled.div`
    width: 100%;
    text-align: center;
`

const StyledCalendar = styled(Calendar)`
    width: 100% !important;
    border: none;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    font-family: 'Roboto', sans-serif;

    .react-calendar__navigation {
        margin-bottom: 0;
    }

    .react-calendar__navigation {
        display: flex;
        height: 44px;
    }

    .react-calendar__navigation button {
        min-width: 44px;
        background: none;
        font-size: 16px;
        color: var(--primary-color);
        font-weight: bold;
    }

    .react-calendar__navigation button:enabled:hover,
    .react-calendar__navigation button:enabled:focus {
        background-color: rgba(180, 0, 0, 0.1);
    }

    .react-calendar__month-view {
        height: 100%;

        & > div {
            height: 100%;

            & > div {
                height: 100%;
            }
        }
    }

    .react-calendar__month-view__weekdays {
        text-align: center;
        font-weight: bold;
        color: #666;
    }

    .react-calendar__month-view__days {
        height: calc(100% - 34px);
    }

    .react-calendar__month-view__days__day--weekend {
        color: #b40000;
    }

    .react-calendar__month-view__weekdays__weekday {
        color: #b40000;
    }

    .react-calendar__tile:enabled:hover,
    .react-calendar__tile:enabled:focus {
        background-color: #b400001a;
    }

    .react-calendar__tile--active {
        background-color: var(--primary-color);
        color: white;
    }

    .react-calendar__tile--active abbr,
    .react-calendar__tile--active span,
    .react-calendar__tile--active div {
        color: white;
    }

    .has-tournaments {
        background-color: #b400000d;
        font-weight: bold;
    }

    .has-tournaments:hover {
        background-color: #b4000026;
    }

    .react-calendar__tile--active,
    .react-calendar__tile--active:enabled:hover,
    .react-calendar__tile--active:enabled:focus {
        background-color: #b4000088 !important;
    }

    @media (max-width: ${Breakpoint.TABLET_L}) {
        .react-calendar__month-view__weekdays {
            font-size: 0.9rem;
        }
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        .react-calendar__month-view__weekdays {
            font-size: 0.8rem;
        }

        .react-calendar__tile {
            font-size: 0.9rem;
            padding: 4px;
        }
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        height: 328px;

        .react-calendar__viewContainer {
            height: calc(100% - 44px);
        }

        .react-calendar__month-view__weekdays {
            font-size: 0.7rem;
        }

        .react-calendar__tile {
            font-size: 0.8rem;
            padding: 2px;
        }
    }
`

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
`

const ModalContent = styled.div`
    background: white;
    border-radius: 8px;
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        max-width: 95%;
        max-height: 95vh;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        max-width: 100%;
        max-height: 100vh;
        border-radius: 0;
    }
`

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid #ddd;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        padding: 16px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        padding: 12px;
    }
`

const ModalTitle = styled.h2`
    font-size: 1.5rem;
    color: var(--primary-color);
    font-family: 'Roboto Semi Bold';
    margin: 0;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1.2rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1rem;
    }
`

const CloseButton = styled.button`
    background: none;
    border: none;
    color: #666;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
        background-color: #f0f0f0;
        color: #333;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1rem;
        padding: 6px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 0.9rem;
        padding: 4px;
    }
`

const ModalBody = styled.div`
    padding: 24px;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        padding: 16px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        padding: 12px;
    }
`

const NoData = styled.div`
    text-align: center;
    padding: 32px;
    color: #666;
    font-size: 1.2rem;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        padding: 24px;
        font-size: 1rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        padding: 16px;
        font-size: 0.9rem;
    }
`

const TileContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px 0;
    width: 100%;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        gap: 1px;
        padding: 1px 0;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        gap: 1px;
        padding: 1px 0;
    }
`

const TournamentItem = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.65rem;
    line-height: 1.1;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        font-size: 0.55rem;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 0.5rem;
        gap: 2px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 0.45rem;
        gap: 2px;
    }
`

const TournamentName = styled.span`
    color: var(--primary-color);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
`

const TournamentPlace = styled.span`
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        display: none;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        display: none;
    }
`

const UpcomingIndicator = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--primary-color);
    flex-shrink: 0;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        width: 4px;
        height: 4px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        width: 3px;
        height: 3px;
    }
`

const MoreTournaments = styled.span`
    font-size: 0.6rem;
    color: #999;
    text-align: center;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 0.5rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 0.45rem;
    }
`

const DesktopTileContent = styled.div`
    @media (max-width: ${Breakpoint.PHONE}) {
        display: none;
    }
`

const MobileTileContent = styled.div`
    display: none;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2px;
    justify-content: center;
    align-items: center;
    padding: 2px 0;

    @media (max-width: ${Breakpoint.PHONE}) {
        display: flex;
    }
`

const TournamentDot = styled.div<{ isComing: boolean }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${(props) => (props.isComing ? 'var(--primary-color)' : '#999')};
    flex-shrink: 0;

    @media (max-width: ${Breakpoint.PHONE}) {
        width: 4px;
        height: 4px;
    }
`

const MoreDots = styled.span`
    font-size: 0.6rem;
    color: #999;

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 0.5rem;
    }
`

export default BadnetTournaments
